import { useEffect } from 'react';
import { setCanonical, setMeta } from '@/utils/seo';
import { BreadcrumbSchema } from '@/components/SchemaMarkup';

export default function AccessibilityPage() {
  useEffect(() => {
    setCanonical('/accessibility');
    setMeta('Accessibility Statement | CompareWorkersComp.com', 'CompareWorkersComp.com is committed to ensuring digital accessibility for all users.');
  }, []);

  return (
    <>
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Accessibility' }]} />
      <section className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4">
          <p className="speakable-intro text-gray-300 text-xs mb-4">CompareWorkersComp.com accessibility statement describes our commitment to digital accessibility for all users.</p>
          <h1 className="text-3xl font-bold text-cwc-navy mb-2" data-testid="accessibility-h1">Accessibility Statement</h1>
          <p className="text-gray-400 text-sm mb-8">Last updated: April 2026</p>

          <div className="prose prose-gray max-w-none space-y-6 text-gray-600 text-sm leading-relaxed">

            <h2 className="text-xl font-bold text-cwc-navy mt-8">Accessibility Commitment</h2>
            <p>CompareWorkersComp.com, operated by Trio Insurance & Financial Services (CA License #0C66738), is committed to ensuring digital accessibility for all users, including people with disabilities. We continually work to improve the user experience for everyone and apply relevant accessibility standards.</p>

            <h2 className="text-xl font-bold text-cwc-navy mt-8">Accessibility Features</h2>
            <p>Our website includes the following accessibility features:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Semantic HTML structure with proper heading hierarchy (H1, H2, H3)</li>
              <li>Descriptive alt text on all meaningful images</li>
              <li>Sufficient color contrast between text and background elements</li>
              <li>Keyboard-navigable interactive elements including buttons, links, and form controls</li>
              <li>Mobile-responsive design that works across all screen sizes and devices</li>
              <li>Clear, readable typography with appropriate text sizing</li>
              <li>Focus indicators on interactive elements for keyboard users</li>
              <li>ARIA attributes on dynamic content where appropriate</li>
              <li>Logical content structure and reading order</li>
              <li>Built-in accessibility widget with text resizing, grayscale, high contrast, and readable font options</li>
            </ul>

            <h2 className="text-xl font-bold text-cwc-navy mt-8">Standards We Follow</h2>
            <p>We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. These guidelines explain how to make web content more accessible for people with disabilities and more user-friendly for everyone.</p>

            <h2 className="text-xl font-bold text-cwc-navy mt-8">Compatibility</h2>
            <p>Our website is designed to be compatible with the following assistive technologies:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Screen readers (such as NVDA, JAWS, and VoiceOver)</li>
              <li>Screen magnification software</li>
              <li>Speech recognition software</li>
              <li>Operating system accessibility features</li>
            </ul>

            <h2 className="text-xl font-bold text-cwc-navy mt-8">Known Limitations</h2>
            <p>While we strive for full accessibility, some areas may have limitations:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Some dynamically generated content may not be immediately available to screen readers</li>
              <li>The AI chatbot (Sarah) responses are text-based and may not be fully optimized for all screen reader configurations</li>
            </ul>
            <p>We are actively working to address any known limitations.</p>

            <h2 className="text-xl font-bold text-cwc-navy mt-8">European Accessibility Act (EAA) and ADA Compliance</h2>
            <p>Trio Insurance & Financial Services recognizes the importance of the European Accessibility Act (EAA) and the Americans with Disabilities Act (ADA). While our primary audience is in the United States, we are committed to providing an accessible experience for all users regardless of location.</p>

            <h2 className="text-xl font-bold text-cwc-navy mt-8">Feedback and Contact</h2>
            <p>We welcome your feedback on the accessibility of our website. If you encounter any accessibility barriers or have suggestions for improvement, please contact us:</p>
            <p><strong>Email:</strong> <a href="mailto:info@trioins.com" className="text-red-600 underline">info@trioins.com</a> (Subject: Accessibility Feedback)</p>
            <p><strong>Phone:</strong> <a href="tel:+14159282886" className="text-red-600 underline">(415) 928-2886</a></p>
            <p>We aim to respond to accessibility feedback within 5 business days.</p>
          </div>
        </div>
      </section>
    </>
  );
}
